import type { ReactElement } from 'react';
import React from 'react';
import Link from 'next/link';
import { Text } from '@savvy-kit/foundations/text';

export type BreadcrumbData = {
  href?: string;
  title: string | undefined;
  detail?: ReactElement;
  icon?: ReactElement;
  tooltip?: ReactElement;
};

export const formatBreadcrumbList = (crumbs: BreadcrumbData[]) => {
  if (crumbs.length === 0) {
    return [];
  }

  const breadcrumbs = crumbs.slice(0, -1).map((crumb) => formatInactiveBreadcrumb(crumb));

  const lastCrumb = crumbs[crumbs.length - 1];
  if (lastCrumb) {
    breadcrumbs.push(formatActiveBreadcrumb(lastCrumb));
  }

  return breadcrumbs;
};

export const formatInactiveBreadcrumb = (crumb: BreadcrumbData) => {
  const { href, title } = crumb;
  const key = `inactive-${title ?? 'untitled'}-${href ?? 'nohref'}`;

  const titleText = (
    <Text key={`${key}-text`} variant="subtitle-lg" color="secondary" className="hover:underline">
      {title}
    </Text>
  );

  if (!href) {
    return titleText;
  }

  return (
    <Link key={key} href={href} className="no-underline">
      {titleText}
    </Link>
  );
};

export const formatActiveBreadcrumb = (crumb: BreadcrumbData) => {
  const { href, title, detail, icon, tooltip } = crumb;
  const key = `active-${title ?? 'untitled'}-${href ?? 'nohref'}`;

  const titleText = (
    <div key={`${key}-stack`} className="flex flex-row gap-0 items-center">
      {icon}
      <Text variant="subtitle-lg" className={icon ? 'ml-2 mr-2' : 'mr-2'}>
        {title}
      </Text>
      {tooltip}
      {detail}
    </div>
  );

  if (!href) {
    return titleText;
  }

  return (
    <Link key={key} href={href} className="no-underline">
      {titleText}
    </Link>
  );
};
